// Страница «Приложение»: как заведение выглядит в гостевом приложении Resstro.
// Владелец настраивает видимость, логотип, обложки, описание, кухни и «выбор
// гостей». Справа — живое превью карточки в ленте и шапки экрана заведения,
// которое обновляется по мере правок.
//
// Состояние: `draft` хранит только изменённые поля поверх серверного значения
// заведения (est из ['admin/ests']), список кухонь — отдельным драфтом поверх
// ['admin/est-cuisines', id]. Пока поле не тронуто, показывается серверное
// значение, поэтому переключение заведения (key={est.id}) и рефетч после
// сохранения не требуют синхронизации через эффекты.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  AlertTriangle, ArrowDown, ArrowUp, Check, ChefHat, Eye, EyeOff, Heart, ImagePlus, Images,
  Save, Search, Smartphone, Sparkles, Text, Upload, UtensilsCrossed, X,
} from 'lucide-react';

import { PhotosField } from '@/components/PhotosField';
import { Spinner } from '@/components/ui/Spinner';
import {
  getEstablishmentCuisines, listAppCuisines, listDishes, listEstablishments,
  setEstablishmentCuisines, updateEstablishment, uploadImage,
  type AppCuisine, type Dish, type Establishment,
} from '@/services/admin';
import { useEstablishmentStore } from '@/store/establishment.store';
import { formatRub } from '@/utils/format';
import { asAbsoluteUrl } from '@/utils/url';

// Лимиты — зеркало серверной валидации (admin.routes appCardSchema,
// setEstablishmentCuisinesSchema) и того, сколько реально показывает iOS.
const MAX_COVERS = 8;
const MAX_CUISINES = 10;
const MAX_FEATURED = 8; // iOS показывает первые 8; сервер принимает до 12
const MIN_FEATURED_TO_SHOW = 2; // секция «Выбор гостей» появляется от 2 блюд
const MAX_DESCRIPTION = 300;
const AUTO_COVERS = 5; // сколько фото блюд подставляем, если обложек нет

type CardFields = Pick<
  Establishment,
  'logoUrl' | 'showInApp' | 'description' | 'covers' | 'featuredDishIds'
>;

export default function AppCardPage() {
  const { data: ests, isLoading } = useQuery({ queryKey: ['admin/ests'], queryFn: listEstablishments });
  const currentId = useEstablishmentStore((s) => s.currentId);

  if (isLoading) return <Spinner label="Загружаем заведение…" />;

  const est = ests?.find((e) => e.id === currentId) ?? ests?.[0] ?? null;
  if (!est) {
    return (
      <div className="card p-8 text-center text-slate-500">
        Сначала создайте заведение в разделе «Настройки».
      </div>
    );
  }

  // key — сброс черновика при переключении заведения в сайдбаре.
  return <AppCardEditor key={est.id} est={est} />;
}

// ─── Редактор ─────────────────────────────────────────────────────────
function AppCardEditor({ est }: { est: Establishment }) {
  const qc = useQueryClient();

  const { data: allCuisines = [] } = useQuery({
    queryKey: ['admin/app-cuisines'],
    queryFn: listAppCuisines,
    staleTime: 5 * 60_000,
  });
  const { data: serverCuisineIds } = useQuery({
    queryKey: ['admin/est-cuisines', est.id],
    queryFn: () => getEstablishmentCuisines(est.id),
  });
  const { data: dishes = [], isLoading: dishesLoading } = useQuery({
    queryKey: ['admin/dishes', 'app-card'],
    queryFn: () => listDishes(),
  });

  const [draft, setDraft] = useState<Partial<CardFields>>({});
  const [cuisineDraft, setCuisineDraft] = useState<string[] | null>(null);

  const value: CardFields = {
    logoUrl: draft.logoUrl !== undefined ? draft.logoUrl : est.logoUrl,
    showInApp: draft.showInApp ?? est.showInApp,
    description: draft.description !== undefined ? draft.description : est.description,
    covers: draft.covers ?? est.covers,
    featuredDishIds: draft.featuredDishIds ?? est.featuredDishIds,
  };
  const cuisineIds = cuisineDraft ?? serverCuisineIds ?? [];

  const patch = useMemo(() => {
    const out: Partial<CardFields> = {};
    if (draft.logoUrl !== undefined && draft.logoUrl !== est.logoUrl) out.logoUrl = draft.logoUrl;
    if (draft.showInApp !== undefined && draft.showInApp !== est.showInApp) out.showInApp = draft.showInApp;
    if (draft.description !== undefined && (draft.description ?? null) !== (est.description ?? null)) {
      out.description = draft.description;
    }
    if (draft.covers && !sameList(draft.covers, est.covers)) out.covers = draft.covers;
    if (draft.featuredDishIds && !sameList(draft.featuredDishIds, est.featuredDishIds)) {
      out.featuredDishIds = draft.featuredDishIds;
    }
    return out;
  }, [draft, est]);
  const cuisinesChanged = cuisineDraft !== null && !sameList(cuisineDraft, serverCuisineIds ?? []);
  const dirty = Object.keys(patch).length > 0 || cuisinesChanged;

  // Не даём случайно закрыть вкладку с несохранёнными правками.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const save = useMutation({
    mutationFn: async () => {
      const [updated, savedCuisines] = await Promise.all([
        Object.keys(patch).length ? updateEstablishment(est.id, patch) : Promise.resolve(null),
        cuisinesChanged ? setEstablishmentCuisines(est.id, cuisineIds) : Promise.resolve(null),
      ]);
      return { updated, savedCuisines };
    },
    onSuccess: ({ updated, savedCuisines }) => {
      // Обновляем кеш сразу ответом сервера — без «мигания» старыми значениями
      // между сбросом черновика и рефетчем.
      if (updated) {
        qc.setQueryData<Establishment[]>(['admin/ests'], (list) =>
          list ? list.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)) : list,
        );
      }
      if (savedCuisines) qc.setQueryData(['admin/est-cuisines', est.id], savedCuisines);
      setDraft({});
      setCuisineDraft(null);
      toast.success('Сохранено — в приложении обновится в течение минуты');
    },
  });

  const set = <K extends keyof CardFields>(k: K, v: CardFields[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const dishById = useMemo(() => new Map(dishes.map((d) => [d.id, d])), [dishes]);
  const autoCovers = useMemo(
    () =>
      dishes
        .filter((d) => d.isAvailable && d.photoUrl)
        .slice(0, AUTO_COVERS)
        .map((d) => d.photoUrl as string),
    [dishes],
  );
  const cuisineById = useMemo(() => new Map(allCuisines.map((c) => [c.id, c])), [allCuisines]);
  const selectedCuisines = cuisineIds.map((id) => cuisineById.get(id)).filter(Boolean) as AppCuisine[];
  const featuredDishes = value.featuredDishIds.map((id) => dishById.get(id)).filter(Boolean) as Dish[];

  const noPlace = !est.city || est.geoLat == null || est.geoLon == null;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Smartphone size={20} /> Приложение
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Как «{est.name}» выглядит в гостевом приложении Resstro: карточка в ленте и экран заведения.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs ${dirty ? 'text-amber-600' : 'text-slate-400'}`}>
            {dirty ? 'Есть несохранённые изменения' : 'Все изменения сохранены'}
          </span>
          <button className="btn-primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            <Save size={16} /> {save.isPending ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-5 min-w-0">
          {/* Видимость */}
          <SectionCard
            icon={value.showInApp ? <Eye size={18} /> : <EyeOff size={18} />}
            title="Видимость в приложении"
          >
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-brand-600"
                checked={value.showInApp}
                onChange={(e) => set('showInApp', e.target.checked)}
              />
              <span>
                <span className="text-sm font-medium">Показывать заведение в приложении</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Главная лента, поиск и экран заведения. Не влияет на карту сайта и QR-меню — гости по QR
                  продолжают заказывать как обычно.
                </span>
              </span>
            </label>
            {value.showInApp && noPlace && (
              <Notice tone="warn">
                Без адреса с координатами заведение не попадёт в ленту — гости ищут по расстоянию. Укажите
                адрес в «Настройки → Заведение», выбрав его из подсказки.
              </Notice>
            )}
          </SectionCard>

          {/* Логотип */}
          <SectionCard icon={<ImagePlus size={18} />} title="Логотип">
            <LogoField value={value.logoUrl} onChange={(url) => set('logoUrl', url)} />
          </SectionCard>

          {/* Обложки */}
          <SectionCard
            icon={<Images size={18} />}
            title="Обложки"
            right={<span className="text-xs text-slate-400">{value.covers.length}/{MAX_COVERS}</span>}
          >
            <p className="text-xs text-slate-500 -mt-2 mb-3">
              Крупные фото в карточке ленты и на экране заведения, листаются свайпом. Первая — главная.
              Лучше горизонтальные, интерьер и фирменные блюда.
            </p>
            <PhotosField
              kind="cover"
              max={MAX_COVERS}
              aspect="wide"
              coverLabel="Главная"
              value={value.covers}
              onChange={(covers) => set('covers', covers)}
              hint="JPG, PNG, WebP, HEIC — до 15 МБ. Сервер сожмёт до 1600 px."
            />
            {value.covers.length === 0 && (
              <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-3">
                <div className="text-xs text-slate-600 flex items-center gap-1.5">
                  <Sparkles size={14} className="text-brand-500" />
                  Пока обложек нет — покажем фото блюд автоматически:
                </div>
                {autoCovers.length ? (
                  <div className="flex gap-1.5 mt-2 overflow-x-auto">
                    {autoCovers.map((u) => (
                      <img
                        key={u}
                        src={asAbsoluteUrl(u) ?? undefined}
                        alt=""
                        className="h-14 w-20 rounded-md object-cover shrink-0 border border-slate-200"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-amber-600 mt-1">
                    У блюд тоже нет фото — карточка будет без картинки. Загрузите хотя бы одну обложку.
                  </div>
                )}
              </div>
            )}
          </SectionCard>

          {/* Описание */}
          <SectionCard
            icon={<Text size={18} />}
            title="Описание"
            right={
              <span className={`text-xs ${(value.description?.length ?? 0) > MAX_DESCRIPTION ? 'text-rose-600' : 'text-slate-400'}`}>
                {value.description?.length ?? 0}/{MAX_DESCRIPTION}
              </span>
            }
          >
            <textarea
              className="input min-h-[88px] resize-y"
              placeholder="Например: «Домашняя грузинская кухня, хинкали лепим при вас. Летняя веранда, детское меню»."
              value={value.description ?? ''}
              maxLength={MAX_DESCRIPTION}
              onChange={(e) => set('description', e.target.value === '' ? null : e.target.value)}
            />
            <p className="text-xs text-slate-500 mt-1.5">
              Показывается под названием на экране заведения. Коротко, о главном — 1–2 предложения.
            </p>
          </SectionCard>

          {/* Кухни */}
          <SectionCard
            icon={<ChefHat size={18} />}
            title="Кухни"
            right={<span className="text-xs text-slate-400">{cuisineIds.length}/{MAX_CUISINES}</span>}
          >
            <p className="text-xs text-slate-500 -mt-2 mb-3">
              По кухням гости фильтруют ленту. Выберите то, что действительно готовите — первые две
              показываются в карточке.
            </p>
            <CuisinePicker
              all={allCuisines}
              selected={cuisineIds}
              onChange={setCuisineDraft}
              disabled={serverCuisineIds === undefined}
            />
          </SectionCard>

          {/* Выбор гостей */}
          <SectionCard
            icon={<UtensilsCrossed size={18} />}
            title="Выбор гостей"
            right={<span className="text-xs text-slate-400">{value.featuredDishIds.length}/{MAX_FEATURED}</span>}
          >
            <p className="text-xs text-slate-500 -mt-2 mb-3">
              Первая секция меню на экране заведения. Если ничего не выбрать — соберём автоматически из
              самых заказываемых блюд за 30 дней. Секция появляется от {MIN_FEATURED_TO_SHOW} блюд; блюда
              без фото и снятые с продажи в неё не попадают.
            </p>
            {dishesLoading ? (
              <Spinner label="Загружаем меню…" />
            ) : (
              <FeaturedPicker
                dishes={dishes}
                selected={value.featuredDishIds}
                onChange={(ids) => set('featuredDishIds', ids)}
              />
            )}
          </SectionCard>
        </div>

        <div className="xl:sticky xl:top-4 self-start">
          <PhonePreview
            name={est.name}
            city={est.city}
            logoUrl={value.logoUrl}
            covers={value.covers.length ? value.covers : autoCovers}
            description={value.description}
            cuisines={selectedCuisines}
            featured={featuredDishes}
            hidden={!value.showInApp}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Кусочки формы ────────────────────────────────────────────────────
function SectionCard({
  icon, title, right, children,
}: {
  icon: React.ReactNode;
  title: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2">
          {icon} {title}
        </h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function Notice({ tone, children }: { tone: 'warn' | 'info'; children: React.ReactNode }) {
  const cls = tone === 'warn'
    ? 'bg-amber-50 border-amber-200 text-amber-800'
    : 'bg-slate-50 border-slate-200 text-slate-700';
  return (
    <div className={`mt-3 flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${cls}`}>
      <AlertTriangle size={14} className="mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function LogoField({ value, onChange }: { value: string | null; onChange: (url: string | null) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (f: File | null) => {
    if (!f) return;
    setUploading(true);
    try {
      const res = await uploadImage('logo', f);
      onChange(res.url);
    } catch {
      /* обработано interceptor-ом */
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="flex items-start gap-4">
      <div className="w-20 h-20 rounded-full border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
        {value ? (
          <img src={asAbsoluteUrl(value) ?? undefined} alt="" className="w-full h-full object-cover" />
        ) : (
          <ImagePlus size={24} className="text-slate-300" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          hidden
          onChange={(e) => onPick(e.target.files?.[0] ?? null)}
        />
        <div className="flex gap-2 flex-wrap">
          <button className="btn-outline" disabled={uploading} onClick={() => fileRef.current?.click()}>
            <Upload size={16} /> {uploading ? 'Загрузка…' : value ? 'Заменить' : 'Загрузить'}
          </button>
          {value && (
            <button className="btn-ghost text-rose-600" onClick={() => onChange(null)}>
              <X size={16} /> Убрать
            </button>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-1.5">
          В приложении логотип круглый — квадратная картинка с полями по краям смотрится лучше всего. Общий
          с QR-меню и лендингом.
        </p>
      </div>
    </div>
  );
}

function CuisinePicker({
  all, selected, onChange, disabled,
}: {
  all: AppCuisine[];
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}) {
  if (!all.length) {
    return <div className="text-sm text-slate-500">Справочник кухонь пуст — его заполняет супер-админ.</div>;
  }
  const isOn = new Set(selected);
  const full = selected.length >= MAX_CUISINES;

  const toggle = (id: string) => {
    if (isOn.has(id)) onChange(selected.filter((x) => x !== id));
    else if (full) toast.error(`Не больше ${MAX_CUISINES} кухонь`);
    else onChange([...selected, id]);
  };

  return (
    <div className="flex flex-wrap gap-2">
      {all.map((c) => {
        const on = isOn.has(c.id);
        return (
          <button
            key={c.id}
            type="button"
            disabled={disabled}
            onClick={() => toggle(c.id)}
            aria-pressed={on}
            className={`inline-flex items-center gap-2 rounded-full border pl-1 pr-3 py-1 text-sm transition disabled:opacity-50 ${
              on
                ? 'border-brand-600 bg-brand-600 text-white'
                : full
                  ? 'border-slate-200 text-slate-400'
                  : 'border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            {c.imageUrl ? (
              <img src={asAbsoluteUrl(c.imageUrl) ?? undefined} alt="" className="h-6 w-6 rounded-full object-cover" />
            ) : (
              <span className={`h-6 w-6 rounded-full flex items-center justify-center ${on ? 'bg-white/20' : 'bg-slate-100'}`}>
                <ChefHat size={12} />
              </span>
            )}
            {c.name}
            {on && <Check size={14} />}
          </button>
        );
      })}
    </div>
  );
}

function FeaturedPicker({
  dishes, selected, onChange,
}: {
  dishes: Dish[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const [q, setQ] = useState('');
  const byId = useMemo(() => new Map(dishes.map((d) => [d.id, d])), [dishes]);
  const chosen = selected.map((id) => byId.get(id)).filter(Boolean) as Dish[];
  const isOn = new Set(selected);
  const full = selected.length >= MAX_FEATURED;

  const needle = q.trim().toLowerCase();
  const candidates = useMemo(
    () => dishes.filter((d) => !needle || d.name.toLowerCase().includes(needle)),
    [dishes, needle],
  );

  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= selected.length) return;
    const next = [...selected];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const toggle = (id: string) => {
    if (isOn.has(id)) onChange(selected.filter((x) => x !== id));
    else if (full) toast.error(`Не больше ${MAX_FEATURED} блюд`);
    else onChange([...selected, id]);
  };

  if (!dishes.length) {
    return <div className="text-sm text-slate-500">В меню пока нет блюд — добавьте их в разделе «Меню».</div>;
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div>
        <div className="label">Выбрано · порядок как в приложении</div>
        {chosen.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 p-4 text-xs text-slate-500 text-center">
            Ничего не выбрано — секция соберётся автоматически.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {chosen.map((d, i) => (
              <li key={d.id} className="flex items-center gap-2 rounded-lg border border-slate-200 p-1.5 pr-2">
                <DishThumb dish={d} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{d.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    {formatRub(d.discountPrice ?? d.price)}
                    {!d.isAvailable && <span className="text-amber-600">снято с продажи</span>}
                    {!d.photoUrl && <span className="text-amber-600">без фото</span>}
                  </div>
                </div>
                <button className="btn-ghost p-1" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Выше">
                  <ArrowUp size={14} />
                </button>
                <button
                  className="btn-ghost p-1"
                  disabled={i === chosen.length - 1}
                  onClick={() => move(i, 1)}
                  aria-label="Ниже"
                >
                  <ArrowDown size={14} />
                </button>
                <button className="btn-ghost p-1 text-rose-600" onClick={() => toggle(d.id)} aria-label="Убрать">
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
        {chosen.length === 1 && (
          <div className="text-xs text-amber-600 mt-2">
            Одного блюда мало — секция покажется, когда выбрано хотя бы {MIN_FEATURED_TO_SHOW}.
          </div>
        )}
      </div>

      <div>
        <div className="label">Меню</div>
        <div className="relative mb-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="input pl-8"
            placeholder="Поиск по названию…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <ul className="max-h-[320px] overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
          {candidates.length === 0 && (
            <li className="p-3 text-xs text-slate-500 text-center">Ничего не найдено</li>
          )}
          {candidates.map((d) => {
            const on = isOn.has(d.id);
            return (
              <li key={d.id}>
                <button
                  type="button"
                  onClick={() => toggle(d.id)}
                  aria-pressed={on}
                  className={`w-full flex items-center gap-2 p-1.5 pr-3 text-left transition ${
                    on ? 'bg-brand-50' : 'hover:bg-slate-50'
                  } ${!on && full ? 'opacity-50' : ''}`}
                >
                  <DishThumb dish={d} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{d.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2">
                      {formatRub(d.discountPrice ?? d.price)}
                      {!d.isAvailable && <span className="text-amber-600">снято с продажи</span>}
                    </div>
                  </div>
                  <span
                    className={`h-5 w-5 rounded-md border flex items-center justify-center shrink-0 ${
                      on ? 'bg-brand-600 border-brand-600 text-white' : 'border-slate-300'
                    }`}
                  >
                    {on && <Check size={12} />}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function DishThumb({ dish }: { dish: Dish }) {
  return dish.photoUrl ? (
    <img
      src={asAbsoluteUrl(dish.photoUrl) ?? undefined}
      alt=""
      className="h-10 w-10 rounded-md object-cover shrink-0 bg-slate-100"
    />
  ) : (
    <div className="h-10 w-10 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
      <UtensilsCrossed size={14} className="text-slate-300" />
    </div>
  );
}

// ─── Превью телефона ──────────────────────────────────────────────────
function PhonePreview({
  name, city, logoUrl, covers, description, cuisines, featured, hidden,
}: {
  name: string;
  city: string | null;
  logoUrl: string | null;
  covers: string[];
  description: string | null;
  cuisines: AppCuisine[];
  featured: Dish[];
  hidden: boolean;
}) {
  const cover = covers[0] ?? null;
  const visibleFeatured = featured.filter((d) => d.isAvailable && d.photoUrl);

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Smartphone size={16} /> Превью
        </h3>
        <span className="text-xs text-slate-400">iOS · обновляется по мере правок</span>
      </div>

      <div className="relative mx-auto w-full max-w-[300px] rounded-[28px] border-[6px] border-slate-900 bg-[#f4f4f6] overflow-hidden shadow-card">
        <div className="absolute left-1/2 -translate-x-1/2 top-1.5 h-4 w-20 rounded-full bg-slate-900 z-10" />
        <div className={`pt-8 pb-5 px-3 space-y-4 ${hidden ? 'opacity-40 grayscale' : ''}`}>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5 px-0.5">Лента · главная</div>
            <div className="rounded-2xl bg-white overflow-hidden shadow-sm">
              <div className="relative aspect-[16/10] bg-slate-200">
                <PreviewImage url={cover} />
                <div className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 flex items-center justify-center">
                  <Heart size={14} className="text-slate-700" />
                </div>
                {covers.length > 1 && (
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                    {covers.slice(0, 6).map((u, i) => (
                      <span key={u} className={`h-1.5 rounded-full ${i === 0 ? 'w-3 bg-white' : 'w-1.5 bg-white/60'}`} />
                    ))}
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-[15px] truncate">{name}</div>
                  <div className="text-[11px] text-slate-500 shrink-0">1,2 км</div>
                </div>
                <div className="text-xs text-slate-500 truncate mt-0.5">
                  {cuisines.length ? cuisines.slice(0, 2).map((c) => c.name).join(' · ') : city ?? 'Кухня не указана'}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5 px-0.5">Экран заведения</div>
            <div className="rounded-2xl bg-white overflow-hidden shadow-sm">
              <div className="relative aspect-[16/9] bg-slate-200">
                <PreviewImage url={cover} />
              </div>
              <div className="px-3 pb-3 -mt-6 relative">
                <div className="h-12 w-12 rounded-full border-[3px] border-white bg-white overflow-hidden shadow-sm flex items-center justify-center">
                  {logoUrl ? (
                    <img src={asAbsoluteUrl(logoUrl) ?? undefined} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-sm font-bold text-slate-500">{name.slice(0, 1).toUpperCase()}</span>
                  )}
                </div>
                <div className="font-semibold text-[15px] mt-1.5 leading-tight">{name}</div>
                {description ? (
                  <p className="text-xs text-slate-600 mt-1 leading-snug line-clamp-3">{description}</p>
                ) : (
                  <p className="text-xs text-slate-300 mt-1 italic">Описание появится здесь</p>
                )}
                {cuisines.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {cuisines.map((c) => (
                      <span key={c.id} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">
                        {c.name}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-3">
                  <div className="text-xs font-semibold mb-1.5">Выбор гостей</div>
                  {visibleFeatured.length >= MIN_FEATURED_TO_SHOW ? (
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {visibleFeatured.map((d) => (
                        <div key={d.id} className="w-[88px] shrink-0">
                          <img
                            src={asAbsoluteUrl(d.photoUrl!) ?? undefined}
                            alt=""
                            className="h-[72px] w-[88px] rounded-xl object-cover bg-slate-100"
                          />
                          <div className="text-[11px] font-medium mt-1 truncate">{d.name}</div>
                          <div className="text-[10px] text-slate-500">{formatRub(d.discountPrice ?? d.price)}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-[11px] text-slate-400">
                      {featured.length ? 'Мало блюд с фото — покажем популярные автоматически' : 'Автоматически — популярные за 30 дней'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {hidden && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="rounded-full bg-slate-900/85 text-white text-xs px-3 py-1.5 flex items-center gap-1.5">
              <EyeOff size={14} /> Скрыто из приложения
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewImage({ url }: { url: string | null }) {
  return url ? (
    <img src={asAbsoluteUrl(url) ?? undefined} alt="" className="absolute inset-0 h-full w-full object-cover" />
  ) : (
    <div className="absolute inset-0 flex items-center justify-center text-slate-400">
      <ImagePlus size={22} />
    </div>
  );
}

function sameList(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
